/**
 * DemoVoiceModal — демо голосового ввода для непремиум пользователей
 * Соответствует mobile DemoVoiceModal по структуре и стилю
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icon';
import { PremiumUpgradeModal } from './PremiumFeature/PremiumUpgradeModal';
import { getDemoVoiceInput } from '../utils/premiumDemoData';
import './DemoVoiceModal.css';

interface DemoVoiceModalProps {
  visible: boolean;
  onClose: () => void;
  /** При нажатии "Получить Premium" — открыть модалку подписки (опционально) */
  onRequestPremium?: () => void;
}

export function DemoVoiceModal({ visible, onClose, onRequestPremium }: DemoVoiceModalProps) {
  const { t } = useTranslation();
  const demoData = getDemoVoiceInput();
  const { recognizedExample, currencySymbol } = demoData;

  const [step, setStep] = useState<'recording' | 'processing' | 'result'>('recording');
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  const handlePremiumClick = () => {
    if (onRequestPremium) {
      onClose();
      onRequestPremium();
    } else {
      setShowPremiumModal(true);
    }
  };

  useEffect(() => {
    if (!visible) return;
    const t1 = setTimeout(() => setStep('processing'), 2000);
    return () => clearTimeout(t1);
  }, [visible]);

  useEffect(() => {
    if (step !== 'processing') return;
    const t2 = setTimeout(() => setStep('result'), 1500);
    return () => clearTimeout(t2);
  }, [step]);

  useEffect(() => {
    if (!visible) setStep('recording');
  }, [visible]);

  const formatAmount = (amount: number) =>
    currencySymbol === '$' || currencySymbol === '€'
      ? `${amount.toFixed(2)} ${currencySymbol}`
      : `${amount.toLocaleString()} ${currencySymbol}`;

  if (!visible) return null;

  return createPortal(
    <>
      <div className="demo-modal-overlay" onClick={onClose}>
        <div className="demo-modal" onClick={(e) => e.stopPropagation()}>
          <button className="demo-modal__close" onClick={onClose} aria-label={t('common.close')}>
            ×
          </button>
          <h2 className="demo-modal__title">{t('home.voiceInput')}</h2>

          <div className="demo-modal__content">
            {step === 'recording' && (
              <div className="demo-voice-recording">
                <div className="demo-voice-mic demo-voice-mic--pulse">
                  <Icon name="mic" size={48} color="#FFFFFF" />
                </div>
                <p className="demo-voice-label">{t('quickVoice.recording')}</p>
                <p className="demo-voice-time">0:02</p>
                <p className="demo-voice-example">"{recognizedExample.text}"</p>
              </div>
            )}

            {step === 'processing' && (
              <div className="demo-voice-processing">
                <div className="demo-voice-icon">
                  <Icon name="message-circle" size={48} color="var(--color-primary)" />
                </div>
                <p>{t('voiceInput.analyzingWithAI')}</p>
              </div>
            )}

            {step === 'result' && (
              <div className="demo-voice-result">
                <Icon name="check-circle" size={64} color="#34C759" />
                <p className="demo-voice-recognized-label">{t('voiceInput.recognized')}</p>
                <p className="demo-voice-recognized-text">"{recognizedExample.text}"</p>
                <div className="demo-voice-parsed">
                  <div className="demo-voice-parsed-row">
                    <Icon name="credit-card" size={20} color="#FF3B30" />
                    <span>{recognizedExample.parsed.title}</span>
                  </div>
                  <p className="demo-voice-parsed-amount">-{formatAmount(recognizedExample.parsed.amount)}</p>
                </div>
              </div>
            )}
          </div>

          <div className="demo-modal__badge">{t('premium.demoMode')}</div>

          <button className="demo-modal__premium-btn" onClick={handlePremiumClick}>
            <Icon name="star" size={20} color="#FFFFFF" />
            {t('premium.unlockFeature')}
            <Icon name="chevron-right" size={20} color="#FFFFFF" />
          </button>
        </div>
      </div>

      {!onRequestPremium && (
        <PremiumUpgradeModal
          isOpen={showPremiumModal}
          onClose={() => setShowPremiumModal(false)}
          featureId="voice_input"
          onUpgrade={() => {
            setShowPremiumModal(false);
            onClose();
          }}
        />
      )}
    </>,
    document.body
  );
}
